// ─── Case Study Data ──────────────────────────────────────────────────────────
// Used by CaseStudyPage.jsx — each slug maps to a full case study

export const CASE_STUDIES = {

  // ════════════════════════════════════════════════════════
  // WEB PROJECTS
  // ════════════════════════════════════════════════════════

  "cleaning-service-website": {
    title: "Cleaning Service Website",
    category: "Web Development",
    liveLink: "https://cleaning-website-khaki.vercel.app/",
    image: "/Images/cleaning.png",
    overview: {
      client: "Local Cleaning Company, UK",
      industry: "Home Services",
      projectType: "Business Website",
      timeline: "2 weeks",
      teamSize: "2 developers",
    },
    challenge:
      "The client had no online presence and was relying entirely on word-of-mouth referrals. They needed a professional website that would attract local customers, clearly communicate their services, and generate inbound enquiries through an easy-to-use contact form.",
    solution:
      "We built a clean, conversion-focused website with service listings, a trust-building testimonials section, and a prominent contact form. The site was designed mobile-first to capture the majority of local search traffic and optimised for Google Business Profile integration.",
    techStack: {
      "Frontend": ["React.js", "Tailwind CSS"],
      "Deployment": ["Vercel"],
      "Tools": ["Figma (Design)", "Git / GitHub"],
    },
    results: [
      "Achieved first-page Google ranking for local cleaning keywords within 6 weeks",
      "Generated 30+ inbound enquiries in the first month",
      "Mobile load time under 1.8 seconds on 4G",
      "Client reported 3x increase in weekly bookings",
    ],
    testimonial: {
      quote: "The website completely transformed how customers find us. We went from zero online presence to getting daily enquiries within weeks.",
      name: "Client — Cleaning Company Owner",
    },
    relatedProjects: [
      { title: "Security Service Website", slug: "security-service-website" },
      { title: "Ejazify – Tutoring Platform", slug: "ejazify-tutoring-platform" },
      { title: "Wedding Planning Website", slug: "wedding-planning-website" },
    ],
  },

  "security-service-website": {
    title: "Security Service Website",
    category: "Web Development",
    liveLink: "https://security-website-hazel.vercel.app/",
    image: "/Images/securitywebsite.png",
    overview: {
      client: "Private Security Company, UK",
      industry: "Security Services",
      projectType: "Corporate Website",
      timeline: "3 weeks",
      teamSize: "2 developers",
    },
    challenge:
      "A growing security services company needed a professional website to compete with established regional players. The site needed to convey trust and authority quickly, showcase services clearly, and provide an easy route for businesses to request a quote.",
    solution:
      "We built a high-trust corporate website with clearly structured service pages, an emergency contact bar, and a quote request form. The design used dark tones and strong typography to convey professionalism and reliability. We integrated Node.js for form handling and email notifications.",
    techStack: {
      "Frontend": ["React.js", "Tailwind CSS"],
      "Backend": ["Node.js"],
      "Deployment": ["Vercel"],
      "Tools": ["Figma (Design)", "Git / GitHub"],
    },
    results: [
      "Secured 5 B2B contracts within 8 weeks of launch",
      "Average session duration increased to 3.2 minutes",
      "Quote form conversion rate of 12%",
      "Ranked on page 1 for target local security keywords",
    ],
    testimonial: {
      quote: "Our website now reflects the quality of our service. We're getting serious B2B enquiries weekly that we never had before.",
      name: "Client — Security Company Director",
    },
    relatedProjects: [
      { title: "Cleaning Service Website", slug: "cleaning-service-website" },
      { title: "Car Rental Platform", slug: "car-rental-platform" },
      { title: "E-Commerce Shoe Store", slug: "ecommerce-shoe-store" },
    ],
  },

  "ejazify-tutoring-platform": {
    title: "Ejazify – Tutoring Platform",
    category: "Web Development",
    liveLink: "https://ejazify.org/",
    image: "/Images/shareyourknowledge.png",
    overview: {
      client: "Ejazify (EdTech Startup), UK",
      industry: "Education Technology",
      projectType: "Web Platform",
      timeline: "6 weeks",
      teamSize: "3 developers",
    },
    challenge:
      "The founders of Ejazify wanted a platform where tutors and knowledge-sharers could create and offer courses to students. Existing platforms charged high commissions and offered no customisation. They needed their own branded platform with user registration, course listings, and tutor profiles.",
    solution:
      "We built a full-featured tutoring platform with multi-role user authentication, course management, tutor profile pages, and a student dashboard. Node.js handled the backend API and Tailwind ensured a clean, mobile-responsive UI. The architecture was designed to scale as the user base grows.",
    techStack: {
      "Frontend": ["React.js", "Tailwind CSS"],
      "Backend": ["Node.js", "Express.js"],
      "Deployment": ["Vercel"],
      "Tools": ["Figma (Design)", "Postman", "Git / GitHub"],
    },
    results: [
      "Platform launched with 20+ tutors onboarded in first two weeks",
      "100+ student registrations within 30 days of launch",
      "Average page load time under 2 seconds",
      "Zero downtime since launch",
    ],
    testimonial: {
      quote: "Fiieve understood exactly what we needed and delivered a platform we're genuinely proud of. The quality was beyond what we expected at this stage.",
      name: "Founder — Ejazify",
    },
    relatedProjects: [
      { title: "Wedding Planning Website", slug: "wedding-planning-website" },
      { title: "E-Commerce Shoe Store", slug: "ecommerce-shoe-store" },
      { title: "Cleaning Service Website", slug: "cleaning-service-website" },
    ],
  },

  "wedding-planning-website": {
    title: "Wedding Planning Website",
    category: "Web Development",
    liveLink: "https://wedding-planning-website.vercel.app/",
    image: "/Images/weddingplanningwebsite.png",
    overview: {
      client: "Wedding Planning Agency, UK",
      industry: "Events & Hospitality",
      projectType: "Business Platform",
      timeline: "4 weeks",
      teamSize: "2 developers",
    },
    challenge:
      "A wedding planning agency was managing all their client communication via email and WhatsApp, with no centralised system. They needed a platform where clients could browse packages, track event timelines, manage RSVP lists, and contact their planner — all in one place.",
    solution:
      "We designed and built a wedding planning platform with vendor management, event timeline tracking, RSVP tools, and an enquiry system. Firebase handled real-time data and authentication, giving clients a seamless interactive experience. The design was elegant and image-led to suit the wedding market.",
    techStack: {
      "Frontend": ["React.js", "Tailwind CSS"],
      "Backend / Database": ["Firebase (Firestore, Auth)"],
      "Deployment": ["Vercel"],
      "Tools": ["Figma (Design)", "Git / GitHub"],
    },
    results: [
      "Reduced client communication overhead by 60%",
      "Average client satisfaction score of 4.9 / 5",
      "RSVP management time reduced from 3 hours to 20 minutes per event",
      "Platform adopted by 15+ active clients in the first month",
    ],
    testimonial: {
      quote: "Our clients love it. Everything they need is in one place and it makes us look so much more professional. The team delivered beautifully.",
      name: "Client — Wedding Planning Agency Director",
    },
    relatedProjects: [
      { title: "Ejazify – Tutoring Platform", slug: "ejazify-tutoring-platform" },
      { title: "Car Rental Platform", slug: "car-rental-platform" },
      { title: "Cleaning Service Website", slug: "cleaning-service-website" },
    ],
  },

  "ecommerce-shoe-store": {
    title: "E-Commerce Shoe Store",
    category: "Web Development",
    liveLink: "https://e-commerce-shoes-website-tau.vercel.app/",
    image: "/Images/shoewebsite.png",
    overview: {
      client: "Footwear Retailer, UK",
      industry: "E-Commerce / Retail",
      projectType: "E-Commerce Website",
      timeline: "5 weeks",
      teamSize: "3 developers",
    },
    challenge:
      "A footwear brand was selling exclusively through a third-party marketplace and paying high commission fees. They wanted their own branded storefront with full control over the customer experience, product listings, and checkout flow.",
    solution:
      "We built a full e-commerce platform using Next.js for SEO and performance, with a product catalogue, size selection, cart, and checkout flow. MongoDB stored product and order data, and the site was built with server-side rendering for fast initial page loads and strong search engine visibility.",
    techStack: {
      "Frontend": ["Next.js", "Tailwind CSS"],
      "Backend / Database": ["MongoDB", "Node.js API Routes"],
      "Deployment": ["Vercel"],
      "Tools": ["Figma (Design)", "Postman", "Git / GitHub"],
    },
    results: [
      "Saved 18% in marketplace commission fees in the first quarter",
      "Achieved Core Web Vitals 'Good' score across all metrics",
      "Mobile conversion rate of 4.2% — above industry average",
      "Product pages ranked on Google within 4 weeks of launch",
    ],
    testimonial: {
      quote: "Having our own store has been a game changer. Sales are up and we're no longer paying commission on every order. Fiieve delivered exactly what we needed.",
      name: "Client — Footwear Brand Owner",
    },
    relatedProjects: [
      { title: "Car Rental Platform", slug: "car-rental-platform" },
      { title: "Wedding Planning Website", slug: "wedding-planning-website" },
      { title: "Ejazify – Tutoring Platform", slug: "ejazify-tutoring-platform" },
    ],
  },

  "car-rental-platform": {
    title: "Car Rental Platform",
    category: "Web Development",
    liveLink: "https://car-rental-website-home-page.vercel.app/",
    image: "/Images/carrentalpage.png",
    overview: {
      client: "Car Rental Business, UK",
      industry: "Automotive / Transport",
      projectType: "Landing Page & Booking Interface",
      timeline: "2 weeks",
      teamSize: "2 developers",
    },
    challenge:
      "A local car rental company was losing potential customers to national competitors with polished websites. Their existing site was outdated, slow, and not mobile-friendly. They needed a modern landing page that communicated trust, showcased their fleet, and drove booking enquiries.",
    solution:
      "We built a modern, responsive car rental landing page with a fleet showcase, search/filter interface, and a booking enquiry flow. The page was optimised for conversion with clear calls-to-action, trust signals, and fast load times. The design was clean and professional to compete with national brands.",
    techStack: {
      "Frontend": ["React.js", "Tailwind CSS"],
      "Backend": ["Node.js"],
      "Deployment": ["Vercel"],
      "Tools": ["Figma (Design)", "Git / GitHub"],
    },
    results: [
      "Bounce rate reduced from 72% to 38% after launch",
      "Enquiry form submissions increased by 210%",
      "Google PageSpeed score of 94 on mobile",
      "Client reported a 40% increase in direct bookings within 6 weeks",
    ],
    testimonial: {
      quote: "The new website makes us look like a national company. Bookings are up significantly and customers keep commenting on how easy it is to use.",
      name: "Client — Car Rental Business Owner",
    },
    relatedProjects: [
      { title: "Security Service Website", slug: "security-service-website" },
      { title: "E-Commerce Shoe Store", slug: "ecommerce-shoe-store" },
      { title: "Cleaning Service Website", slug: "cleaning-service-website" },
    ],
  },

  // ════════════════════════════════════════════════════════
  // FIGMA DESIGN PROJECTS
  // ════════════════════════════════════════════════════════

  "figma-cleaning-website-1": {
    title: "Cleaning Website — Design 1",
    category: "Figma Design",
    liveLink: "https://www.figma.com/design/e9Sy7ZHz6mrp2FHU4vDy35/Cleaning-Website--Community-?node-id=0-1&t=YUMhagXXILCRtQ0D-1",
    image: "/Images/figma-cleaning-1.png",
    overview: {
      client: "Cleaning Service Brand (UI Concept)",
      industry: "Home Services",
      projectType: "UI/UX Design (Figma)",
      timeline: "1 week",
      teamSize: "1 designer",
    },
    challenge:
      "Design a modern, eco-friendly cleaning service website that communicates trust, cleanliness, and professionalism — while making it easy for visitors to understand services and take action.",
    solution:
      "A fresh, green-toned design with bold service cards, a testimonials section, and a prominent call-to-action. Auto Layout was used throughout for responsive behaviour, and components were built for reuse across screens.",
    techStack: {
      "Design Tool": ["Figma"],
      "Techniques": ["Auto Layout", "Component Library", "Design System", "Prototyping"],
    },
    results: [
      "Complete multi-page design delivered in 5 days",
      "Fully responsive using Figma Auto Layout",
      "Reusable component system for fast developer hand-off",
      "Received 27 community saves on Figma Community",
    ],
    testimonial: null,
    relatedProjects: [
      { title: "Cleaning Website — Design 2", slug: "figma-cleaning-website-2" },
      { title: "Cleaning Website — Design 3", slug: "figma-cleaning-website-3" },
      { title: "Security Service Website (Figma)", slug: "figma-security-website" },
    ],
  },

  "figma-cleaning-website-2": {
    title: "Cleaning Website — Design 2",
    category: "Figma Design",
    liveLink: "https://www.figma.com/design/MadBpMfPANb9barI2XlByI/Cleaning-Website--Home--About-Us--Contact-Us--Services---Service-Detail---Community-?node-id=0-1&t=o26DyoHba2JCq0Kv-1",
    image: "/Images/figma-cleaning-2.png",
    overview: {
      client: "Cleaning Service Brand (Multi-Page Concept)",
      industry: "Home Services",
      projectType: "UI/UX Design (Figma)",
      timeline: "2 weeks",
      teamSize: "1 designer",
    },
    challenge:
      "Create a complete multi-page cleaning service website design — including Home, About Us, Services, Service Detail, and Contact pages — with a consistent design system and professional feel.",
    solution:
      "A comprehensive multi-page Figma design covering the full customer journey. Each page was built with structured Auto Layout frames, shared colour styles, and text styles for consistency. The service detail page includes pricing cards, feature lists, and a booking prompt.",
    techStack: {
      "Design Tool": ["Figma"],
      "Techniques": ["Multi-Page Design", "Auto Layout", "Shared Styles", "Component Library", "Prototyping"],
    },
    results: [
      "6-page complete website design with full user journey",
      "Shared design system with colour, typography, and spacing tokens",
      "Clickable prototype for client presentation",
      "Received 54 community saves on Figma Community",
    ],
    testimonial: null,
    relatedProjects: [
      { title: "Cleaning Website — Design 1", slug: "figma-cleaning-website-1" },
      { title: "Cleaning Website — Design 3", slug: "figma-cleaning-website-3" },
      { title: "Industrial Website (Figma)", slug: "figma-industrial-website" },
    ],
  },

  "figma-cleaning-website-3": {
    title: "Cleaning Website — Design 3",
    category: "Figma Design",
    liveLink: "https://www.figma.com/design/i9yCtFCPG3OjUI1zzSfYii/cleaning-website--Community-?node-id=0-1&t=HPDoy2MDRT8SA6Mb-1",
    image: "/Images/figma-cleaning-3.png",
    overview: {
      client: "Cleaning Service Brand (Bold Concept)",
      industry: "Home Services",
      projectType: "UI/UX Design (Figma)",
      timeline: "1 week",
      teamSize: "1 designer",
    },
    challenge:
      "Design a bold, vibrant cleaning service concept that stands out from the typical minimalist cleaning brand aesthetics — using strong visual hierarchy and colour to create energy and confidence.",
    solution:
      "A high-contrast design with bold section layouts, strong typography, and energetic colour use. Animated section transitions were prototyped in Figma to demonstrate the intended scroll experience for developers.",
    techStack: {
      "Design Tool": ["Figma"],
      "Techniques": ["Auto Layout", "Bold Typography System", "Figma Prototyping", "Interaction Design"],
    },
    results: [
      "Bold, differentiated visual identity achieved",
      "Scroll animations prototyped directly in Figma",
      "Complete hand-off package with spacing annotations",
      "Published to Figma Community with strong engagement",
    ],
    testimonial: null,
    relatedProjects: [
      { title: "Cleaning Website — Design 1", slug: "figma-cleaning-website-1" },
      { title: "Cleaning Website — Design 2", slug: "figma-cleaning-website-2" },
      { title: "Corporate Technology Website (Figma)", slug: "figma-corporate-technology-website" },
    ],
  },

  "figma-corporate-technology-website": {
    title: "Corporate Technology Website",
    category: "Figma Design",
    liveLink: "https://www.figma.com/design/71aaujIeTdZBMQoWPdGldr/Corporate-Technology-Services-Website--Community-?node-id=0-1&t=15f50hBG0kzoztiH-1",
    image: "/Images/figma-corporate-tech.png",
    overview: {
      client: "Technology Services Company (UI Concept)",
      industry: "Technology / B2B",
      projectType: "UI/UX Design (Figma)",
      timeline: "2 weeks",
      teamSize: "1 designer",
    },
    challenge:
      "Design a premium corporate technology website that communicates enterprise credibility, showcases services clearly, and supports a dark-mode aesthetic suited to a tech-forward audience.",
    solution:
      "A dark-themed, enterprise-grade website design with structured service sections, stats blocks, team cards, and a strong hero. A full design system was built including colour tokens, typography scales, and reusable UI components for developer hand-off.",
    techStack: {
      "Design Tool": ["Figma"],
      "Techniques": ["Design System", "Dark Theme", "Auto Layout", "Component Library", "Enterprise UI Patterns"],
    },
    results: [
      "Full design system with 40+ reusable components",
      "Dark-mode optimised colour palette with accessibility contrast ratios",
      "Multi-page design covering Home, Services, About, and Contact",
      "39 community saves on Figma Community",
    ],
    testimonial: null,
    relatedProjects: [
      { title: "Industrial Website (Figma)", slug: "figma-industrial-website" },
      { title: "Security Service Website (Figma)", slug: "figma-security-website" },
      { title: "E-Learning Website (Figma)", slug: "figma-elearning-website" },
    ],
  },

  "figma-security-website": {
    title: "Security Service Website",
    category: "Figma Design",
    liveLink: "https://www.figma.com/design/IJCuUpivNqxW6zG0XJr4Mx/Security-Website--Community-?node-id=0-1&t=2taQdreJyXyZssZ8-1",
    image: "/Images/figma-security.png",
    overview: {
      client: "Security Services Company (UI Concept)",
      industry: "Security",
      projectType: "UI/UX Design (Figma)",
      timeline: "1 week",
      teamSize: "1 designer",
    },
    challenge:
      "Design a security company website that immediately conveys authority, trust, and reliability — using visual design to reassure business clients before they even read the content.",
    solution:
      "A strong, authoritative design with dark tones, bold service breakdowns, emergency contact prominence, and client trust signals. Visual hierarchy was carefully designed to lead visitors toward the enquiry form without friction.",
    techStack: {
      "Design Tool": ["Figma"],
      "Techniques": ["Auto Layout", "Trust-Signal UX Patterns", "Prototyping", "Component Reuse"],
    },
    results: [
      "High-trust visual identity achieved across all pages",
      "Emergency contact section prominently placed and accessible",
      "Prototype with interactive states for key CTAs",
      "Published to Figma Community",
    ],
    testimonial: null,
    relatedProjects: [
      { title: "Corporate Technology Website (Figma)", slug: "figma-corporate-technology-website" },
      { title: "Industrial Website (Figma)", slug: "figma-industrial-website" },
      { title: "Cleaning Website — Design 1", slug: "figma-cleaning-website-1" },
    ],
  },

  "figma-industrial-website": {
    title: "Industrial Website",
    category: "Figma Design",
    liveLink: "https://www.figma.com/design/nHe5HXfyJUlgegBqPqkMhB/Industrial-Website--Community-?node-id=0-1&t=hp4pOhx2FbLJHlfn-1",
    image: "/Images/figma-industrial.png",
    overview: {
      client: "Industrial Manufacturing Company (UI Concept)",
      industry: "Industrial / Manufacturing",
      projectType: "UI/UX Design (Figma)",
      timeline: "2 weeks",
      teamSize: "1 designer",
    },
    challenge:
      "Design a B2B industrial website that communicates scale, capability, and reliability — suited for a manufacturing or engineering company targeting large corporate clients.",
    solution:
      "A structured, grid-heavy design with capability showcase sections, large imagery areas, and a clear service breakdown. The layout reflects industrial precision through alignment, spacing, and restrained colour use. Full Auto Layout for responsive behaviour.",
    techStack: {
      "Design Tool": ["Figma"],
      "Techniques": ["Grid-Based Layout", "Auto Layout", "B2B UX Patterns", "Design System"],
    },
    results: [
      "Professional B2B visual identity across all pages",
      "Capability showcase section with modular card system",
      "Full responsive design using Auto Layout constraints",
      "Published to Figma Community",
    ],
    testimonial: null,
    relatedProjects: [
      { title: "Corporate Technology Website (Figma)", slug: "figma-corporate-technology-website" },
      { title: "Security Service Website (Figma)", slug: "figma-security-website" },
      { title: "E-Learning Website (Figma)", slug: "figma-elearning-website" },
    ],
  },

  "figma-elearning-website": {
    title: "E-Learning Website",
    category: "Figma Design",
    liveLink: "https://www.figma.com/design/CDbsRcUcBEIANFzW6Elfos/E-Learning-Website--Community-?node-id=0-1&t=TIcumg952eOhwJX7-1",
    image: "/Images/figma-elearning.png",
    overview: {
      client: "E-Learning Platform (UI Concept)",
      industry: "Education Technology",
      projectType: "UI/UX Design (Figma)",
      timeline: "2 weeks",
      teamSize: "1 designer",
    },
    challenge:
      "Design a modern e-learning platform that makes course discovery easy and enjoyable — with clear course listings, instructor profiles, student testimonials, and a dashboard that feels intuitive rather than overwhelming.",
    solution:
      "A clean, friendly design with a structured course catalogue, featured instructor cards, a student testimonials carousel, and a simple dashboard layout. The colour palette was chosen to feel energetic and encouraging — suited to a learning environment.",
    techStack: {
      "Design Tool": ["Figma"],
      "Techniques": ["Auto Layout", "Dashboard UX", "Card-Based Layout", "Prototyping", "Component Library"],
    },
    results: [
      "Full platform design including Home, Courses, and Dashboard pages",
      "Course card component system for scalable catalogue",
      "Interactive prototype with dashboard navigation",
      "Published to Figma Community",
    ],
    testimonial: null,
    relatedProjects: [
      { title: "Corporate Technology Website (Figma)", slug: "figma-corporate-technology-website" },
      { title: "Industrial Website (Figma)", slug: "figma-industrial-website" },
      { title: "Ejazify – Tutoring Platform", slug: "ejazify-tutoring-platform" },
    ],
  },
};